import { buildPlan, LENDER_MATCH, planToMarkdown } from '../../report/actionPlan.js';
import { STATUS_LABELS } from '../../readiness/rules.js';
import { Disclaimer, PageHead } from '../components.js';
import { useWorkspace } from '../state.js';

function download(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}

function Rows({ rows }: { rows: Array<[string, string]> }) {
  return <dl className="kv">{rows.map(([k, v]) => <div key={k} style={{ display: 'contents' }}><dt>{k}</dt><dd style={{ textAlign: 'right' }}>{v}</dd></div>)}</dl>;
}

export function Plan() {
  const { ws } = useWorkspace();
  const plan = buildPlan(ws);
  const date = new Date(plan.generatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <>
      <div className="no-print">
        <PageHead eyebrow="Action plan" title="Your financing action plan">
          Everything to take into a lender conversation, in one place. Print it or save it as a file.
        </PageHead>
        <div className="row" style={{ marginBottom: 20 }}>
          <button className="btn primary" type="button" onClick={() => window.print()}>Print or save as PDF</button>
          <button className="btn" type="button" onClick={() => download('financing-action-plan.md', planToMarkdown(plan), 'text/markdown')}>Download as text</button>
        </div>
      </div>

      <article className="card plan">
        <header>
          <div className="eyebrow">Tyche Business · Financing assistant</div>
          <h1 style={{ marginTop: 8 }}>Financing action plan</h1>
          <p className="muted">Prepared {date}.</p>
          <Disclaimer />
        </header>

        <h2>Financing request</h2>
        {plan.request.length ? <Rows rows={plan.request} /> : <p className="muted">Not set yet — <a href="#/assistant">start with the assistant</a>.</p>}

        {plan.business.length > 0 && <><h2>Business profile</h2><Rows rows={plan.business} /></>}

        <h2>Known financials</h2>
        {plan.figures.length === 0
          ? <p className="muted">No figures yet. <a href="#/documents">Add documents or enter key figures.</a></p>
          : (
            <div className="table-scroll">
              <table className="table">
                <thead><tr><th>Figure</th><th className="r">Amount</th><th>Source</th></tr></thead>
                <tbody>{plan.figures.map((f) => (
                  <tr key={f.label}><td>{f.label}</td><td className="r">{f.value}</td><td className="small">{f.source}{!f.confirmed && <span className="badge warn" style={{ marginLeft: 6 }}>unconfirmed</span>}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        {plan.conflicts.length > 0 && (
          <div className="callout warn" style={{ marginTop: 12 }}>
            <strong>Figures that differ between documents</strong>
            <ul>{plan.conflicts.map((c) => <li key={c}>{c}</li>)}</ul>
          </div>
        )}

        <h2>Estimate</h2>
        <ul>{plan.estimate.map((e) => <li key={e}>{e}</li>)}</ul>

        {plan.coverage.length > 0 && <><h2>Cash-flow coverage</h2><ul>{plan.coverage.map((c) => <li key={c}>{c}</li>)}</ul></>}

        <h2>Programs to ask a lender about</h2>
        <p className="small muted">Based only on what the money is for and each program&rsquo;s published maximum. Whether any fits is the lender&rsquo;s call.</p>
        <ul>
          {plan.pathways.map((p) => (
            <li key={p.name}>
              <strong>{p.name}.</strong> {p.why} {p.maxText} {p.notes.join(' ')} <a href={p.citation} target="_blank" rel="noreferrer">sba.gov</a>
            </li>
          ))}
        </ul>

        <h2>Still to do</h2>
        {plan.missing.length === 0
          ? <p>Everything on your checklist is ready.</p>
          : <ul>{plan.missing.map((m) => <li key={m.id}><strong>{m.title}</strong> ({STATUS_LABELS[m.status].toLowerCase()}){m.missing ? `: ${m.missing}` : ''}</li>)}</ul>}

        <h2>Documents to bring</h2>
        <ul>{plan.documentsToBring.map((d) => <li key={d}>{d}</li>)}</ul>

        <h2>Questions to ask a lender</h2>
        {plan.questions.map((q) => (
          <div key={q.topic}>
            <h3 style={{ marginTop: 14 }}>{q.topic}</h3>
            <ul>{q.items.map((i) => <li key={i}>{i}</li>)}</ul>
          </div>
        ))}

        <h2>Next steps</h2>
        <ol>{plan.nextSteps.map((s) => <li key={s}>{s}</li>)}</ol>
        <p className="no-print">
          <a className="btn primary" href={LENDER_MATCH.url} target="_blank" rel="noreferrer">Go to SBA Lender Match</a>
        </p>
      </article>
    </>
  );
}
